// The F&B menu setup screen (spec/13 §6.1, F-FNB-01/-02/-03): the venue's categories and products as
// a list grouped by category, each row with its diet marker, price and an "available" switch that
// flips sold out **at this venue** without editing the product (F-FNB-03) — at once, with Undo; the
// organisation's other venues keep selling it. Adding a product or a category opens a centred dialog, never
// a form stacked over the list. A product and a category take an availability schedule — windows in the
// venue's timezone (F-FNB-02) — and a product its channels and whether it is returnable (F-FNB-01).
//
// The menu is organisation-scoped (spec/13 §6.1), so this edits the organisation's menu through the
// venue's `organizationId`. `products:read` shows it; `products:manage` adds; `products:availability`
// flips the switch. The server checks each again on every request (spec/13 §1). Variants, modifiers and
// per-venue price overrides are edited through the API for now.
//
// A product's **picture** (F-FNB-01) is uploaded from its dialog — straight to storage with a signed URL, as a
// venue's photos are — and attached by file id with the product's save. It shows on Sell's cards and the kiosk's
// Food screen (which the station caches for offline); until one is uploaded, both show a tinted initial.
//
// Where stock is tracked at the venue (spec/13 §6.1, §6.4; ADR-0040), editing a product has a second tab, **Stock
// used**: its recipes here — the base, each size and each extra — with their cost and margin (`stock/RecipesTab.tsx`).
// A product sold out because its recipes cannot be made says so on its row ("Sold out: not enough stock").

import type {
  CreateMenuProductRequest,
  FoodDietMarker,
  FoodOrderChannel,
  FoodPreparationArea,
  MenuCategory,
  MenuProduct,
  TimeWindow,
  UpdateMenuCategoryRequest,
  UpdateMenuProductRequest,
} from '@clavian/api-client';
import { useTranslation, useVenueFormatter } from '@clavian/i18n/react';
import { Badge, Button, Checkbox, Dialog, EmptyState, FormSection, SegmentedControl, SelectField, Switch, TextField, useToast } from '@clavian/ui-web';
import { useState, type ChangeEvent, type FormEvent, type ReactNode } from 'react';

import { useApi } from '../../api/client';
import { useAccess } from '../../api/queries';
import { PROBLEM, problemCode } from '../../api/problems';
import { useProblem } from '../../api/use-problem';
import { LoadFailure } from '../../shell/LoadFailure';
import { Icon } from '../../shell/nav-icons';
import { useCurrentVenue } from '../../shell/scope';
import { Forbidden, Notice, Page, SearchField, Section, TableSkeleton, Toolbar, ToolbarSpring } from '../../ui';
import { FileButton } from '../../venue-profile/PhotosControl';
import { PHOTO_TYPES, PhotoUploadError, type Photo, uploadImage } from '../../venue-profile/uploads';
import { DietLegend, DietMarker } from '../diet';
import { ProductArt } from '../product-art';
import { minorToMajor, moneyFrom } from '../money';
import { useServerErrors } from '../server-errors';
import { useVenueMenu } from '../sell/queries';
import { RecipesTab, useRecipesShown } from '../stock/RecipesTab';
import { useCreateCategory, useCreateProduct, useMenuCategories, useMenuProducts, useSetVenueProductAvailability, useUpdateCategory, useUpdateProduct } from './queries';
import { ScheduleField, scheduleSummary, schedulesValid } from './ScheduleField';

import './menu.css';

const PRODUCTS_MANAGE = 'products:manage';
const PRODUCTS_AVAILABILITY = 'products:availability';

const DIET_MARKERS: readonly FoodDietMarker[] = ['none', 'veg', 'egg', 'non_veg', 'vegan'];
const PREPARATION_AREAS: readonly FoodPreparationArea[] = ['none', 'kitchen', 'bar'];
/** The GST rates the tax category will pick from (F-PAY-11); a stand-in `taxRateBps` until then. */
const TAX_RATES_BPS: readonly number[] = [0, 500, 1200, 1800];
/** Where a product may be sold (F-FNB-01 rule 4); `web` and `api` ride on the app's and the counter's. */
const CHANNELS: readonly FoodOrderChannel[] = ['counter', 'kiosk', 'app'];
const ALL = 'all';

/** Two schedules are the same list of windows. */
const sameWindows = (a: readonly TimeWindow[], b: readonly TimeWindow[]): boolean => JSON.stringify(a) === JSON.stringify(b);

export function MenuPage(): ReactNode {
  const { t } = useTranslation('console');
  const venue = useCurrentVenue();
  const organizationId = venue.organizationId;
  const access = useAccess({ venueId: venue.id });
  const canManage = access.status === 'ready' && access.can(PRODUCTS_MANAGE);
  const canAvailability = access.status === 'ready' && access.can(PRODUCTS_AVAILABILITY);

  const categories = useMenuCategories(organizationId);
  const products = useMenuProducts(organizationId);
  // This venue's own availability and schedule of each product (F-FNB-03, F-FNB-02).
  const venueMenu = useVenueMenu(organizationId, venue.id);
  const atVenue = new Map((venueMenu.data?.products ?? []).map((product) => [product.id, product]));
  const [adding, setAdding] = useState<'product' | 'category' | null>(null);
  const [editing, setEditing] = useState<MenuProduct | null>(null);
  const [editingCategory, setEditingCategory] = useState<MenuCategory | null>(null);
  const [category, setCategory] = useState<string>(ALL);
  const [query, setQuery] = useState('');

  const categoryOptions = (categories.data ?? []).map((c) => ({ value: c.id, label: c.name }));

  let body: ReactNode;
  if (products.data === undefined) {
    if (products.isError && problemCode(products.error) === PROBLEM.forbidden) {
      body = <Forbidden message={t('menu.notPermitted')} />;
    } else {
      body = products.isError ? <LoadFailure error={products.error} message={t('menu.unavailable')} onRetry={() => void products.refetch()} /> : <TableSkeleton />;
    }
  } else if (products.data.length === 0) {
    body = (
      <EmptyState
        icon={<Icon name="cup" />}
        title={t('menu.emptyTitle')}
        description={categories.data !== undefined && categories.data.length === 0 ? t('menu.noCategories') : t('menu.empty')}
        action={
          canManage ? (
            <Button variant="primary" icon={<Icon name="plus" />} onClick={() => setAdding(categoryOptions.length === 0 ? 'category' : 'product')}>
              {categoryOptions.length === 0 ? t('menu.category.title') : t('menu.product.title')}
            </Button>
          ) : undefined
        }
      />
    );
  } else {
    const needle = query.trim().toLowerCase();
    const shown = products.data.filter((product) => (category === ALL || product.categoryId === category) && (needle === '' || product.name.toLowerCase().includes(needle)));
    const byCategory = new Map<string, MenuProduct[]>();
    for (const product of shown) byCategory.set(product.categoryId, [...(byCategory.get(product.categoryId) ?? []), product]);
    const groups = (categories.data ?? []).filter((item) => (byCategory.get(item.id) ?? []).length > 0);
    body = (
      <>
        <Toolbar>
          <SearchField label={t('menu.search')} value={query} onChange={(event) => setQuery(event.target.value)} />
          {(categories.data ?? []).length > 1 ? (
            <SegmentedControl
              label={t('menu.categoriesLabel')}
              value={category}
              onValueChange={setCategory}
              options={[
                { value: ALL, label: t('menu.all'), count: products.data.length },
                ...(categories.data ?? []).map((item) => ({ value: item.id, label: item.name, count: products.data.filter((product) => product.categoryId === item.id).length })),
              ]}
            />
          ) : null}
          <ToolbarSpring />
          <DietLegend />
        </Toolbar>
        {groups.length === 0 ? (
          <EmptyState size="compact" title={t('common.noMatches', { query: query.trim() })} />
        ) : (
          <div className="console-menu-groups">
            {groups.map((item) => (
              <Section
                key={item.id}
                title={item.name}
                description={t('menu.groupCount', { count: (byCategory.get(item.id) ?? []).length })}
                body="rows"
                actions={
                  canManage ? (
                    <Button variant="ghost" size="sm" icon={<Icon name="pencil" />} onClick={() => setEditingCategory(item)} label={t('menu.category.editNamed', { name: item.name })}>
                      {t('menu.category.edit.action')}
                    </Button>
                  ) : undefined
                }
              >
                <ul className="console-menu-list">
                  {(byCategory.get(item.id) ?? []).map((product) => (
                    <ProductRow
                      key={product.id}
                      product={product}
                      atVenue={atVenue.get(product.id)}
                      organizationId={organizationId}
                      venueId={venue.id}
                      canAvailability={canAvailability}
                      canManage={canManage}
                      onEdit={() => setEditing(product)}
                    />
                  ))}
                </ul>
              </Section>
            ))}
          </div>
        )}
      </>
    );
  }

  return (
    <Page
      title={t('menu.title')}
      description={t('menu.intro')}
      actions={
        canManage ? (
          <>
            <Button variant="secondary" onClick={() => setAdding('category')}>
              {t('menu.category.title')}
            </Button>
            <Button variant="primary" icon={<Icon name="plus" />} disabled={categoryOptions.length === 0} onClick={() => setAdding('product')}>
              {t('menu.product.title')}
            </Button>
          </>
        ) : undefined
      }
    >
      {access.status === 'ready' && !canManage ? <Notice role="note">{t('menu.readOnly')}</Notice> : null}
      {body}
      <Dialog open={adding === 'category'} onOpenChange={(open) => setAdding(open ? 'category' : null)} size="sm" dirty="auto" title={t('menu.category.title')} description={t('menu.category.hint')}>
        {adding === 'category' ? <CategoryForm organizationId={organizationId} onDone={() => setAdding(null)} /> : null}
      </Dialog>
      <Dialog open={editingCategory !== null} onOpenChange={(open) => (open ? undefined : setEditingCategory(null))} size="sm" dirty="auto" eyebrow={editingCategory?.name} title={t('menu.category.edit.title')} description={t('menu.category.edit.hint')}>
        {editingCategory !== null ? <CategoryForm organizationId={organizationId} editing={editingCategory} onDone={() => setEditingCategory(null)} /> : null}
      </Dialog>
      <Dialog open={adding === 'product'} onOpenChange={(open) => setAdding(open ? 'product' : null)} size="lg" dirty="auto" title={t('menu.product.title')} description={t('menu.product.hint')}>
        {adding === 'product' ? <ProductForm organizationId={organizationId} categoryOptions={categoryOptions} onDone={() => setAdding(null)} /> : null}
      </Dialog>
      <Dialog open={editing !== null} onOpenChange={(open) => (open ? undefined : setEditing(null))} size="lg" dirty="auto" eyebrow={editing?.name} title={t('menu.product.edit.title')} description={t('menu.product.edit.hint')}>
        {editing !== null ? <ProductEditor organizationId={organizationId} categoryOptions={categoryOptions} editing={editing} onDone={() => setEditing(null)} /> : null}
      </Dialog>
    </Page>
  );
}

function ProductRow({
  product,
  atVenue,
  organizationId,
  venueId,
  canAvailability,
  canManage,
  onEdit,
}: {
  readonly product: MenuProduct;
  /** The product as this venue offers it — its own sold-out switch and schedule — once read. */
  readonly atVenue: MenuProduct | undefined;
  readonly organizationId: string;
  readonly venueId: string;
  readonly canAvailability: boolean;
  readonly canManage: boolean;
  readonly onEdit: () => void;
}): ReactNode {
  const { t } = useTranslation('console');
  const problem = useProblem();
  const format = useVenueFormatter();
  const { show } = useToast();
  const availability = useSetVenueProductAvailability(organizationId, venueId);
  const available = atVenue?.available ?? product.available;
  const notNow = atVenue !== undefined && atVenue.available && !atVenue.inSchedule;

  // Reversible, so it happens at once and offers Undo (BRAND.md §6.1).
  const set = (available: boolean, undoable: boolean) =>
    availability.mutate(
      { productId: product.id, available },
      {
        onSuccess: () =>
          show({
            tone: 'ok',
            title: available ? t('menu.nowAvailable', { name: product.name }) : t('menu.nowSoldOut', { name: product.name }),
            ...(undoable ? { action: { label: t('common.undo'), onAction: () => set(!available, false) } } : {}),
          }),
        onError: (failure) => show({ tone: 'danger', title: problem.text(failure, { fallback: t('menu.availabilityRefused') }) }),
      },
    );

  return (
    <li className="console-menu-item">
      <span className="console-menu-item__marker">
        <ProductArt id={product.id} name={product.name} image={product.image} sizes="2.5rem" className="console-menu-item__art" />
        <DietMarker marker={product.dietMarker} />
      </span>
      <span className="console-menu-item__name">
        {product.name}
        {!available ? (
          <Badge tone="warn" dot>
            {atVenue?.soldOutForStock === true ? t('menu.soldOutForStock') : t('menu.soldOut')}
          </Badge>
        ) : null}
        {notNow ? <Badge tone="neutral">{t('menu.schedule.notNow')}</Badge> : null}
        {product.availabilityWindows.length > 0 ? <span className="console-subtle console-menu-item__schedule">{scheduleSummary(product.availabilityWindows, t)}</span> : null}
      </span>
      <span className="console-menu-item__area console-subtle">{t(`menu.area.${product.preparationArea}`)}</span>
      <span className="console-menu-item__price console-num">{format.money(product.price)}</span>
      <span className="console-menu-item__actions">
        {canManage ? (
          <Button variant="ghost" size="sm" icon={<Icon name="pencil" />} label={t('menu.product.editNamed', { name: product.name })} onClick={onEdit} />
        ) : null}
        <span className="console-menu-item__switch">
          <Switch
            label={t('menu.availableNamed', { name: product.name })}
            checked={available}
            disabled={!canAvailability || availability.isPending}
            onCheckedChange={(checked) => set(checked, true)}
          />
        </span>
      </span>
    </li>
  );
}

function CategoryForm({ organizationId, editing, onDone }: { readonly organizationId: string; readonly editing?: MenuCategory; readonly onDone: () => void }): ReactNode {
  const { t } = useTranslation('console');
  const problem = useProblem();
  const { show } = useToast();
  const create = useCreateCategory(organizationId);
  const update = useUpdateCategory(organizationId);
  const [name, setName] = useState(editing?.name ?? '');
  const [visible, setVisible] = useState(editing?.visible ?? true);
  const [windows, setWindows] = useState<TimeWindow[]>([...(editing?.availabilityWindows ?? [])]);
  const [error, setError] = useState<string | undefined>(undefined);

  function submit(event: FormEvent<HTMLFormElement>): void {
    event.preventDefault();
    if (name.trim() === '') {
      setError(t('menu.category.required'));
      return;
    }
    if (!schedulesValid(windows)) {
      setError(t('menu.schedule.invalid'));
      return;
    }
    setError(undefined);

    if (editing !== undefined) {
      // Only the fields that changed are sent.
      const patch: UpdateMenuCategoryRequest = {};
      if (name.trim() !== editing.name) patch.name = name.trim();
      if (visible !== editing.visible) patch.visible = visible;
      if (!sameWindows(windows, editing.availabilityWindows)) patch.availabilityWindows = windows;
      if (Object.keys(patch).length === 0) {
        setError(t('menu.category.edit.noChanges'));
        return;
      }
      update.mutate(
        { categoryId: editing.id, body: patch },
        {
          onSuccess: () => {
            show({ tone: 'ok', title: t('menu.category.edit.done', { name: name.trim() }) });
            onDone();
          },
          onError: (failure) => setError(problem.fieldMessage(failure, 'name') ?? problem.text(failure, { fallback: t('menu.category.edit.refused') })),
        },
      );
      return;
    }

    create.mutate(
      { name: name.trim(), ...(windows.length === 0 ? {} : { availabilityWindows: windows }) },
      {
        onSuccess: () => {
          show({ tone: 'ok', title: t('menu.category.done', { name: name.trim() }) });
          onDone();
        },
        onError: (failure) => setError(problem.fieldMessage(failure, 'name') ?? problem.text(failure, { fallback: t('menu.category.refused') })),
      },
    );
  }

  return (
    <form className="console-form" aria-label={editing === undefined ? t('menu.category.title') : t('menu.category.edit.title')} noValidate onSubmit={submit}>
      <TextField label={t('menu.category.name')} required value={name} onChange={(event) => setName(event.target.value)} error={error} />
      {editing !== undefined ? <Switch checked={visible} onCheckedChange={setVisible} label={t('menu.category.edit.visible')} /> : null}
      <ScheduleField windows={windows} onChange={setWindows} />
      <div className="console-form__actions console-form__actions--end">
        <Button type="button" variant="ghost" onClick={onDone}>
          {t('common.cancel')}
        </Button>
        <Button type="submit" variant="primary" busy={editing === undefined ? create.isPending : update.isPending}>
          {editing === undefined ? t('menu.category.submit') : t('menu.category.edit.save')}
        </Button>
      </div>
    </form>
  );
}

interface ProductFormState {
  readonly name: string;
  readonly categoryId: string;
  readonly price: string;
  readonly dietMarker: FoodDietMarker;
  readonly preparationArea: FoodPreparationArea;
  readonly taxRateBps: number;
  /** The product's picture: uploaded already, attached with the save; null for none. */
  readonly image: Photo | null;
  /** Where it may be sold (F-FNB-01 rule 4). */
  readonly channels: readonly FoodOrderChannel[];
  /** Whether a sold unit may be returned (F-FNB-01). */
  readonly returnable: boolean;
  /** When it may be sold, in the venue's timezone (F-FNB-02); empty is any time. */
  readonly availabilityWindows: TimeWindow[];
}

/** The request's fields -> the form's inputs, for a validation failure. */
const PRODUCT_SERVER_FIELDS: Readonly<Record<string, string>> = { name: 'name', categoryId: 'categoryId', price: 'price' };

function ProductForm({
  organizationId,
  categoryOptions,
  editing,
  onDone,
}: {
  readonly organizationId: string;
  readonly categoryOptions: readonly { value: string; label: string }[];
  /** The product being edited, or absent when adding a new one. */
  readonly editing?: MenuProduct;
  readonly onDone: () => void;
}): ReactNode {
  const { t } = useTranslation('console');
  const serverErrors = useServerErrors();
  const { show } = useToast();
  const venue = useCurrentVenue();
  const currency = venue.currency ?? 'INR';
  const create = useCreateProduct(organizationId);
  const update = useUpdateProduct(organizationId);

  const initial: ProductFormState =
    editing === undefined
      ? {
          name: '',
          categoryId: categoryOptions[0]?.value ?? '',
          price: '',
          dietMarker: 'none',
          preparationArea: 'none',
          taxRateBps: 0,
          image: null,
          channels: CHANNELS,
          returnable: true,
          availabilityWindows: [],
        }
      : {
          name: editing.name,
          categoryId: editing.categoryId,
          price: minorToMajor(editing.price.amount, currency),
          dietMarker: editing.dietMarker,
          preparationArea: editing.preparationArea,
          taxRateBps: editing.taxRateBps,
          image: editing.image,
          channels: editing.channels,
          returnable: editing.returnable,
          availabilityWindows: [...editing.availabilityWindows],
        };
  const [form, setForm] = useState<ProductFormState>(initial);
  const [errors, setErrors] = useState<{ name?: string; categoryId?: string; price?: string; form?: string }>({});

  function submit(event: FormEvent<HTMLFormElement>): void {
    event.preventDefault();
    const next: { name?: string; categoryId?: string; price?: string } = {};
    if (form.name.trim() === '') next.name = t('menu.product.required');
    if (form.categoryId === '') next.categoryId = t('menu.product.required');
    const price = moneyFrom(form.price, currency);
    if (price === null) next.price = t('menu.product.priceInvalid');
    setErrors(schedulesValid(form.availabilityWindows) ? next : { ...next, form: t('menu.schedule.invalid') });
    if (Object.keys(next).length > 0 || price === null || !schedulesValid(form.availabilityWindows)) return;

    if (editing !== undefined) {
      // Only the fields that changed are sent (the contract's PATCH is `.partial()`).
      const patch: UpdateMenuProductRequest = {};
      if (form.name.trim() !== editing.name) patch.name = form.name.trim();
      if (form.categoryId !== editing.categoryId) patch.categoryId = form.categoryId;
      if (price.amount !== editing.price.amount || price.currency !== editing.price.currency) patch.price = price;
      if (form.taxRateBps !== editing.taxRateBps) patch.taxRateBps = form.taxRateBps;
      if (form.dietMarker !== editing.dietMarker) patch.dietMarker = form.dietMarker;
      if (form.preparationArea !== editing.preparationArea) patch.preparationArea = form.preparationArea;
      if ((form.image?.fileId ?? null) !== (editing.image?.fileId ?? null)) patch.imageFileId = form.image?.fileId ?? null;
      if ([...form.channels].sort().join() !== [...editing.channels].sort().join()) patch.channels = [...form.channels];
      if (form.returnable !== editing.returnable) patch.returnable = form.returnable;
      if (!sameWindows(form.availabilityWindows, editing.availabilityWindows)) patch.availabilityWindows = form.availabilityWindows;
      if (Object.keys(patch).length === 0) {
        setErrors({ form: t('menu.product.edit.noChanges') });
        return;
      }
      update.mutate(
        { productId: editing.id, body: patch },
        {
          onSuccess: () => {
            show({ tone: 'ok', title: t('menu.product.edit.done', { name: form.name.trim() }) });
            onDone();
          },
          onError: (failure) => setErrors(serverErrors(failure, t('menu.product.edit.refused'), PRODUCT_SERVER_FIELDS)),
        },
      );
      return;
    }

    const body: CreateMenuProductRequest = {
      categoryId: form.categoryId,
      name: form.name.trim(),
      price,
      taxRateBps: form.taxRateBps,
      dietMarker: form.dietMarker,
      preparationArea: form.preparationArea,
      channels: [...form.channels],
      returnable: form.returnable,
      ...(form.availabilityWindows.length === 0 ? {} : { availabilityWindows: form.availabilityWindows }),
      ...(form.image === null ? {} : { imageFileId: form.image.fileId }),
    };
    create.mutate(body, {
      onSuccess: () => {
        show({ tone: 'ok', title: t('menu.product.done', { name: form.name.trim() }) });
        onDone();
      },
      onError: (failure) => setErrors(serverErrors(failure, t('menu.product.refused'), PRODUCT_SERVER_FIELDS)),
    });
  }

  return (
    <form className="console-form" aria-label={editing === undefined ? t('menu.product.title') : t('menu.product.edit.title')} noValidate onSubmit={submit}>
      {errors.form !== undefined ? (
        <Notice tone="danger" role="alert">
          {errors.form}
        </Notice>
      ) : null}
      {/* Five things to put a dish on the menu (name, picture, category, price, diet); how it is made, taxed, sold and when wait behind More options. */}
      <FormSection
        more={
          <>
      <div className="console-form__row">
        <SelectField
          label={t('menu.product.preparationArea')}
          options={PREPARATION_AREAS.map((area) => ({ value: area, label: t(`menu.area.${area}`) }))}
          value={form.preparationArea}
          onValueChange={(value) => setForm((c) => ({ ...c, preparationArea: value as FoodPreparationArea }))}
        />
        <SelectField
          label={t('menu.product.tax')}
          options={TAX_RATES_BPS.map((bps) => ({ value: String(bps), label: t('menu.product.taxRate', { rate: bps / 100 }) }))}
          value={String(form.taxRateBps)}
          onValueChange={(value) => setForm((c) => ({ ...c, taxRateBps: Number(value) }))}
        />
      </div>
      <fieldset className="console-menu-channels">
        <legend className="clav-field__label">{t('menu.product.channels')}</legend>
        <p className="console-muted">{t('menu.product.channelsHint')}</p>
        {CHANNELS.map((channel) => (
          <Checkbox
            key={channel}
            label={t(`menu.product.channel.${channel}`)}
            checked={form.channels.includes(channel)}
            onCheckedChange={(checked) =>
              setForm((c) => ({ ...c, channels: checked === true ? CHANNELS.filter((one) => one === channel || c.channels.includes(one)) : c.channels.filter((one) => one !== channel) }))
            }
          />
        ))}
      </fieldset>
      <Switch checked={form.returnable} onCheckedChange={(returnable) => setForm((c) => ({ ...c, returnable }))} label={t('menu.product.returnable')} />
      <ScheduleField windows={form.availabilityWindows} onChange={(availabilityWindows) => setForm((c) => ({ ...c, availabilityWindows }))} />
          </>
        }
      >
      <TextField label={t('menu.product.name')} required value={form.name} onChange={(event) => setForm((c) => ({ ...c, name: event.target.value }))} error={errors.name} />
      <ProductImageField
        organizationId={organizationId}
        productId={editing?.id ?? 'new'}
        name={form.name}
        image={form.image}
        onChange={(image) => setForm((c) => ({ ...c, image }))}
      />
      <div className="console-form__row">
        <SelectField
          label={t('menu.product.category')}
          required
          placeholder={t('menu.product.chooseCategory')}
          options={categoryOptions}
          value={form.categoryId}
          onValueChange={(value) => setForm((c) => ({ ...c, categoryId: value }))}
          error={errors.categoryId}
        />
        <TextField
          label={t('menu.product.price', { currency })}
          required
          numeric
          inputMode="decimal"
          value={form.price}
          onChange={(event) => setForm((c) => ({ ...c, price: event.target.value }))}
          error={errors.price}
        />
      </div>
      <div className="console-field-block">
        <SegmentedControl<FoodDietMarker>
          label={t('menu.product.diet')}
          value={form.dietMarker}
          onValueChange={(value) => setForm((c) => ({ ...c, dietMarker: value }))}
          options={DIET_MARKERS.map((marker) => ({ value: marker, label: t(`menu.diet.${marker}`) }))}
        />
      </div>
      </FormSection>
      <div className="console-form__actions console-form__actions--end">
        <Button type="button" variant="ghost" onClick={onDone}>
          {t('common.cancel')}
        </Button>
        <Button type="submit" variant="primary" busy={editing === undefined ? create.isPending : update.isPending}>
          {editing === undefined ? t('menu.product.submit') : t('menu.product.edit.submit')}
        </Button>
      </div>
    </form>
  );
}

/**
 * Editing a product: its details, and — where stock is tracked here — what it uses from stock (spec/13 §6.1, §6.4).
 * The two stay mounted while the other is showing, so neither loses what was typed in it; each saves on its own.
 */
function ProductEditor({
  organizationId,
  categoryOptions,
  editing,
  onDone,
}: {
  readonly organizationId: string;
  readonly categoryOptions: readonly { value: string; label: string }[];
  readonly editing: MenuProduct;
  readonly onDone: () => void;
}): ReactNode {
  const { t } = useTranslation('console');
  const recipesShown = useRecipesShown();
  const [tab, setTab] = useState<'details' | 'stock'>('details');
  if (!recipesShown) return <ProductForm organizationId={organizationId} categoryOptions={categoryOptions} editing={editing} onDone={onDone} />;
  return (
    <div className="console-product-tabs">
      <SegmentedControl<'details' | 'stock'>
        label={t('menu.product.tabs')}
        value={tab}
        onValueChange={setTab}
        options={[
          { value: 'details', label: t('menu.product.tabDetails') },
          { value: 'stock', label: t('menu.product.tabStock') },
        ]}
      />
      <div className="console-product-tabs__panel" hidden={tab !== 'details'}>
        <ProductForm organizationId={organizationId} categoryOptions={categoryOptions} editing={editing} onDone={onDone} />
      </div>
      <div className="console-product-tabs__panel" hidden={tab !== 'stock'}>
        <RecipesTab product={editing} onDone={onDone} />
      </div>
    </div>
  );
}

/**
 * The product's picture (F-FNB-01): shown as Sell and the kiosk will crop it — a 4:3 frame, filled — with how to
 * shoot one that reads at tile size, and Upload / Replace / Remove. The upload goes straight to storage now; the
 * product's save attaches it.
 */
function ProductImageField({
  organizationId,
  productId,
  name,
  image,
  onChange,
}: {
  readonly organizationId: string;
  readonly productId: string;
  readonly name: string;
  readonly image: Photo | null;
  readonly onChange: (image: Photo | null) => void;
}): ReactNode {
  const { t } = useTranslation('console');
  const problem = useProblem();
  const api = useApi();
  const [busy, setBusy] = useState(false);
  const [status, setStatus] = useState<string | null>(null);

  const add = async (event: ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    event.target.value = '';
    if (file === undefined) return;
    setBusy(true);
    setStatus(t('menu.product.image.uploading', { name: file.name }));
    try {
      onChange(await uploadImage(api, organizationId, { purpose: 'product_image' }, file));
      setStatus(t('menu.product.image.uploaded'));
    } catch (error) {
      setStatus(error instanceof PhotoUploadError ? t(`menu.product.image.${error.reason}`) : problem.text(error, { fallback: t('menu.product.image.failed') }));
    } finally {
      setBusy(false);
    }
  };

  return (
    <fieldset className="console-menu-image">
      <legend className="clav-field__label">{t('menu.product.image.label')}</legend>
      <div className="console-menu-image__body">
        <ProductArt id={productId} name={name === '' ? '?' : name} image={image} sizes="10rem" className="console-menu-image__frame" />
        <div className="console-menu-image__side">
          <p className="console-muted">{t('menu.product.image.hint')}</p>
          <div className="console-menu-image__actions">
            <FileButton label={image === null ? t('menu.product.image.upload') : t('menu.product.image.replace')} busy={busy} onFile={(event) => void add(event)} accept={PHOTO_TYPES} />
            {image === null ? null : (
              <Button type="button" size="sm" variant="ghost" onClick={() => onChange(null)}>
                {t('menu.product.image.remove')}
              </Button>
            )}
          </div>
          <p className="console-muted" role="status">
            {status}
          </p>
        </div>
      </div>
    </fieldset>
  );
}
